import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Search } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { blogArticles, blogCategories } from '../data/blogArticles';
import { SEO_IMAGES, SITE_URL, absoluteUrl, getBlogArticleImagePath } from '../utils/seo';

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState('geral');
  const [query, setQuery] = useState('');

  const filteredArticles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return blogArticles.filter((article) => {
      const matchesCategory = article.category === activeCategory;
      const searchableText = [
        article.title,
        article.description,
        article.specialty,
        article.audience,
        ...(article.keywords || []),
      ]
        .join(' ')
        .toLowerCase();

      return matchesCategory && (!normalizedQuery || searchableText.includes(normalizedQuery));
    });
  }, [activeCategory, query]);

  const recentArticles = blogArticles.slice(0, 6);
  const specialtyArticles = blogArticles.filter((article) => article.category === 'especialidades');

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog de Saúde BuscaDoutor',
    description:
      'Artigos para médicos, dentistas, clínicas e profissionais de saúde sobre temas gerais e especialidades médicas.',
    url: `${SITE_URL}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'BuscaDoutor',
      url: SITE_URL,
    },
    blogPost: blogArticles.map((article) => ({
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.description,
      url: `${SITE_URL}/blog/${article.slug}`,
      dateModified: article.updatedAt,
      image: absoluteUrl(article.image || getBlogArticleImagePath(article.slug)),
    })),
  };

  return (
    <div className="min-h-screen bg-[#F7FBF9]" data-testid="blog-page">
      <SEOHead
        title="Blog de Saúde para Médicos, Dentistas e Clínicas | BuscaDoutor"
        description="Artigos técnicos e estratégicos para profissionais de saúde, organizados por informações gerais e especialidades médicas."
        canonicalUrl="/blog"
        imageUrl={SEO_IMAGES.blog}
        structuredData={schema}
      />

      <Navbar />

      <main>
        <section className="border-b border-[#D9E4E1] bg-[#F5FAF8]">
          <div className="container-app grid gap-8 py-10 md:py-14 lg:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] lg:items-center">
            <div className="max-w-4xl">
              <p className="mb-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-primary">
                <BookOpen className="w-4 h-4" />
                Blog de Saúde
              </p>
              <h1 className="text-4xl md:text-5xl font-semibold text-[#1A2421] leading-tight mb-4">
                Artigos para acompanhar a prática médica, odontológica e a gestão em saúde.
              </h1>
              <p className="text-lg text-[#4F6861] leading-relaxed max-w-3xl">
                Leituras objetivas, baseadas em fontes científicas internacionais, organizadas para
                profissionais que precisam separar novidade útil de ruído.
              </p>
            </div>

            <div className="rounded-[28px] border border-white bg-white p-3 shadow-[0_18px_55px_rgba(28,78,66,0.10)]">
              <img
                src={SEO_IMAGES.blog}
                alt="Blog de saúde do BuscaDoutor com conteúdo técnico para profissionais"
                width="1200"
                height="630"
                className="aspect-[1200/630] w-full rounded-2xl object-cover"
                fetchPriority="high"
              />
            </div>
          </div>
        </section>

        <section className="container-app py-10 md:py-12">
          <div className="grid lg:grid-cols-[minmax(0,760px)_320px] gap-10 lg:gap-14 items-start">
            <div>
              <div className="flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between border-b border-[#D9E4E1] pb-5 mb-2">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary mb-2">Artigos</p>
                  <h2 className="text-2xl font-semibold text-[#1A2421]">Últimas publicações</h2>
                </div>

                <label className="relative block w-full sm:w-[320px]">
                  <span className="sr-only">Buscar artigos</span>
                  <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#59706A]" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Buscar no blog"
                    className="w-full h-11 border-0 border-b border-[#AFC1BC] bg-transparent pl-7 pr-2 text-sm outline-none focus:border-primary"
                    data-testid="blog-search-input"
                  />
                </label>
              </div>

              <div className="flex flex-wrap gap-2 py-5" role="tablist" aria-label="Categorias do blog">
                {blogCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={`rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${
                      activeCategory === category.id
                        ? 'border-primary bg-primary text-white'
                        : 'border-[#D9E4E1] bg-white text-[#425D56] hover:border-primary hover:text-primary'
                    }`}
                    role="tab"
                    aria-selected={activeCategory === category.id}
                    data-testid={`blog-category-${category.id}`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {filteredArticles.length === 0 ? (
                <div className="border-y border-[#D9E4E1] py-8">
                  <p className="font-semibold text-[#1A2421]">Nenhum artigo encontrado.</p>
                  <p className="text-sm text-[#59706A] mt-2">Tente buscar por outra especialidade ou tema.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {filteredArticles.map((article, index) => {
                    const imagePath = article.image || getBlogArticleImagePath(article.slug);
                    return (
                      <article key={article.slug} className="rounded-[22px] border border-[#D9E4E1] bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_14px_38px_rgba(28,78,66,0.08)]" data-testid={`blog-card-${article.slug}`}>
                        <Link to={`/blog/${article.slug}`} className="group grid gap-4 sm:grid-cols-[260px_minmax(0,1fr)]">
                          <div className="overflow-hidden rounded-2xl bg-[#F5FAF8]">
                            <img
                              src={imagePath}
                              alt={`Imagem editorial do artigo: ${article.title}`}
                              width="1200"
                              height="630"
                              loading={index === 0 ? 'eager' : 'lazy'}
                              className="aspect-[1200/630] w-full object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                            />
                          </div>
                          <div className="px-1 py-1 sm:py-2">
                            <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-bold uppercase tracking-[0.12em] text-primary">
                                {article.specialty}
                              </span>
                              <span className="text-[#8DA09B]">/</span>
                              <span className="text-[#71847F]">
                                Atualizado em {new Date(`${article.updatedAt}T12:00:00`).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                            <h3 className="text-xl md:text-[1.45rem] font-semibold text-[#1A2421] leading-snug group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="mt-3 text-sm md:text-base text-[#4F6861] leading-relaxed">{article.description}</p>
                            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary">
                              Ler artigo
                              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                          </div>
                        </Link>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <aside className="lg:sticky lg:top-24 space-y-8">
              <section className="rounded-[22px] border border-[#D9E4E1] bg-white p-5 shadow-sm">
                <h2 className="text-xl font-semibold text-[#1A2421] mb-4">Artigos recentes</h2>
                <div className="space-y-3">
                  {recentArticles.map((article) => (
                    <Link key={article.slug} to={`/blog/${article.slug}`} className="group grid grid-cols-[72px_minmax(0,1fr)] gap-3">
                      <img
                        src={article.image || getBlogArticleImagePath(article.slug)}
                        alt={`Imagem editorial do artigo: ${article.title}`}
                        width="1200"
                        height="630"
                        loading="lazy"
                        className="aspect-[1200/630] rounded-xl bg-[#F5FAF8] object-contain"
                      />
                      <span className="text-sm font-semibold text-[#1A2421] leading-snug group-hover:text-primary">
                        {article.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </section>

              <section className="rounded-[22px] border border-[#D9E4E1] bg-white p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-[#1A2421] mb-4">Especialidades</h2>
                <div className="space-y-2">
                  {specialtyArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/blog/${article.slug}`}
                      className="flex items-center justify-between gap-3 border-b border-[#EDF2F0] py-2 text-sm font-semibold text-[#425D56] hover:text-primary"
                    >
                      {article.specialty}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  ))}
                </div>
              </section>

              <section className="rounded-[22px] bg-primary p-6 text-white shadow-sm">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/70 mb-3">BuscaDoutor</p>
                <h2 className="text-xl font-semibold mb-3">Conteúdo com foco profissional</h2>
                <p className="text-sm leading-relaxed text-white/80">
                  Curadoria para médicos, dentistas e equipes de saúde acompanharem tendências com mais contexto.
                </p>
              </section>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
